/**
 * 家族控除（基礎控除を除く）の合成（バックエンド `FamilyDeductionService` の移植 + #46 拡張）。
 *
 * 控除額の表は `TaxRules` のみが保持し、本モジュールは配偶者控除等・扶養控除・
 * 勤労学生控除・特定親族特別控除・生命保険料控除を合成する。計算本体と明細が同一関数を
 * 共有することで両者の一致を保証する。
 *
 * 扶養親族ごとの合計所得金額が入力された場合、所得要件（所得税は令和8年分〜62万円、
 * 住民税は令和9年度〜62万円）を超える者は扶養控除の対象外とする。19〜23歳で所得要件を
 * 超える者は特定親族特別控除の対象となる（所得税は令和8年分〜・住民税は令和8年度〜。
 * 国税庁 No.1177 / 総務省・自治体周知）。
 */

import type { FamilyData, TaxRules } from './types';

const TEN_THOUSAND = 10000;

/** 家族控除の内訳（計算明細の表示と計算本体で共有する）。 */
export interface FamilyDeductionBreakdown {
  /** 配偶者控除・配偶者特別控除（老人控除対象配偶者を含む）。 */
  spouse: number;
  /** 扶養控除（一般・特定・老人・同居老親等）。 */
  dependents: number;
  /** 勤労学生控除。 */
  workingStudent: number;
  /** 特定親族特別控除（所得税・住民税）。適用時期は年分により異なる。 */
  specificRelatives: number;
  /** 生命保険料控除（一般・介護医療・個人年金）。 */
  lifeInsurance: number;
  /** 合計。 */
  total: number;
}

function emptyBreakdown(): FamilyDeductionBreakdown {
  return { spouse: 0, dependents: 0, workingStudent: 0, specificRelatives: 0, lifeInsurance: 0, total: 0 };
}

function count(value: number): number {
  return Math.max(0, Math.floor(value));
}

/** 扶養親族の人数（所得要件を満たす者のみ）。所得配列が未指定なら全員が満たすものとして扱う。 */
function qualifyingCount(numberOfPeople: number, incomes: number[], thresholdYen: number): number {
  const total = count(numberOfPeople);
  if (incomes.length === 0) {
    return total;
  }
  let qualifying = 0;
  for (let i = 0; i < total; i += 1) {
    if ((incomes[i] ?? 0) * TEN_THOUSAND <= thresholdYen) {
      qualifying += 1;
    }
  }
  return qualifying;
}

/**
 * 特定扶養親族のうち所得要件を満たす人数（特定扶養控除の対象）と、
 * 所得要件を超える者の特定親族特別控除額を返す。
 * 所得税は令和8年分〜、住民税は令和8年度課税（所得年2025）〜適用される。
 */
function specificParts(
  familyData: FamilyData,
  rules: TaxRules,
  incomeTax: boolean,
): { dependentCount: number; specificRelative: number } {
  const total = count(familyData.specificDependents);
  const threshold = incomeTax
    ? rules.dependentIncomeThresholdIncome()
    : rules.dependentIncomeThresholdResident();
  const incomes = familyData.specificDependentIncomes;
  let dependentCount = 0;
  let specificRelative = 0;
  for (let i = 0; i < total; i += 1) {
    const incomeYen = (incomes[i] ?? 0) * TEN_THOUSAND;
    if (incomeYen <= threshold) {
      dependentCount += 1;
    } else {
      specificRelative += incomeTax
        ? rules.specificRelativeDeductionIncome(incomeYen)
        : rules.specificRelativeDeductionResident(incomeYen);
    }
  }
  return { dependentCount, specificRelative };
}

function spouseDeduction(
  familyData: FamilyData,
  taxpayerIncome: number,
  rules: TaxRules,
  incomeTax: boolean,
): number {
  if (!familyData.spouse) {
    return 0;
  }
  const spouseIncome = familyData.spouseIncome * TEN_THOUSAND;
  const elderly = familyData.spouseAge >= 70;
  return incomeTax
    ? rules.spouseDeduction(taxpayerIncome, spouseIncome, elderly)
    : rules.residentSpouseDeduction(taxpayerIncome, spouseIncome, elderly);
}

/**
 * 23歳未満の扶養親族がいるか（生命保険料控除の子育て世帯特例の判定）。
 * 16歳未満・16〜18歳（所得要件を満たす一般扶養親族の内数）・所得要件内の特定扶養親族を対象とする。
 */
function hasChildUnder23(
  familyData: FamilyData,
  qualifyingGeneralCount: number,
  specificDependentCount: number,
): boolean {
  const children16To18 = Math.min(count(familyData.dependents16To18), qualifyingGeneralCount);
  return count(familyData.dependentsUnder16) > 0 || children16To18 > 0 || specificDependentCount > 0;
}

function lifeInsuranceDeduction(
  familyData: FamilyData,
  rules: TaxRules,
  incomeTax: boolean,
  hasChild: boolean,
): number {
  const general = Math.max(0, familyData.lifeInsuranceGeneral);
  const medical = Math.max(0, familyData.lifeInsuranceMedical);
  const pension = Math.max(0, familyData.lifeInsurancePension);
  return incomeTax
    ? rules.lifeInsuranceDeductionIncome(general, medical, pension, hasChild)
    : rules.lifeInsuranceDeductionResident(general, medical, pension);
}

function buildBreakdown(
  familyData: FamilyData | null,
  taxpayerIncome: number,
  rules: TaxRules,
  incomeTax: boolean,
): FamilyDeductionBreakdown {
  if (!familyData) {
    return emptyBreakdown();
  }
  const threshold = incomeTax
    ? rules.dependentIncomeThresholdIncome()
    : rules.dependentIncomeThresholdResident();
  const general = qualifyingCount(familyData.dependents, familyData.dependentIncomes, threshold);
  const specific = specificParts(familyData, rules, incomeTax);
  const elderly = count(familyData.elderlyDependents);
  const elderlyLivingTogether = count(familyData.elderlyDependentsLivingTogether);
  const spouse = spouseDeduction(familyData, taxpayerIncome, rules, incomeTax);
  const dependents = incomeTax
    ? rules.dependentDeductionIncome(general, specific.dependentCount, elderly, elderlyLivingTogether)
    : rules.dependentDeductionResident(general, specific.dependentCount, elderly, elderlyLivingTogether);
  const workingStudent = familyData.workingStudent
    ? incomeTax
      ? rules.workingStudentDeductionIncome()
      : rules.workingStudentDeductionResident()
    : 0;
  const specificRelatives = specific.specificRelative;
  const lifeInsurance = lifeInsuranceDeduction(
    familyData,
    rules,
    incomeTax,
    hasChildUnder23(familyData, general, specific.dependentCount),
  );
  return {
    spouse,
    dependents,
    workingStudent,
    specificRelatives,
    lifeInsurance,
    total: spouse + dependents + workingStudent + specificRelatives + lifeInsurance,
  };
}

/** 所得税の家族控除の内訳。 */
export function incomeTaxFamilyDeductionBreakdown(
  familyData: FamilyData | null,
  taxpayerIncome: number,
  rules: TaxRules,
): FamilyDeductionBreakdown {
  return buildBreakdown(familyData, taxpayerIncome, rules, true);
}

/** 住民税の家族控除の内訳。 */
export function residentTaxFamilyDeductionBreakdown(
  familyData: FamilyData | null,
  taxpayerIncome: number,
  rules: TaxRules,
): FamilyDeductionBreakdown {
  return buildBreakdown(familyData, taxpayerIncome, rules, false);
}

/** 所得税の家族控除合計額（配偶者控除等 + 扶養控除 + 勤労学生控除 + 特定親族特別控除 + 生命保険料控除）。 */
export function incomeTaxFamilyDeductions(
  familyData: FamilyData | null,
  taxpayerIncome: number,
  rules: TaxRules,
): number {
  return incomeTaxFamilyDeductionBreakdown(familyData, taxpayerIncome, rules).total;
}

/** 住民税の家族控除合計額。 */
export function residentTaxFamilyDeductions(
  familyData: FamilyData | null,
  taxpayerIncome: number,
  rules: TaxRules,
): number {
  return residentTaxFamilyDeductionBreakdown(familyData, taxpayerIncome, rules).total;
}

/**
 * 所得税と住民税の人的控除額の差の合計（調整控除の基礎）。
 *
 * 基礎控除の差（一律 5 万円）に、配偶者控除等・扶養控除・勤労学生控除の差を加える。
 * 配偶者特別控除の差は令和8年度課税（所得年2025）以後は 0（総務省・自治体周知）。
 * 生命保険料控除・特定親族特別控除は調整控除の対象外。
 */
export function humanDeductionDifference(
  familyData: FamilyData | null,
  taxpayerIncome: number,
  rules: TaxRules,
): number {
  if (!familyData) {
    return rules.basicDeductionDifference();
  }
  const incomeTax = incomeTaxFamilyDeductionBreakdown(familyData, taxpayerIncome, rules);
  const resident = residentTaxFamilyDeductionBreakdown(familyData, taxpayerIncome, rules);
  const spouseDifference = familyData.spouse
    ? rules.spouseDeductionDifference(
        taxpayerIncome,
        familyData.spouseIncome * TEN_THOUSAND,
        familyData.spouseAge >= 70,
      )
    : 0;
  const dependentsDifference = incomeTax.dependents - resident.dependents;
  const workingStudentDifference = incomeTax.workingStudent - resident.workingStudent;
  return (
    rules.basicDeductionDifference() +
    spouseDifference +
    Math.max(0, dependentsDifference) +
    Math.max(0, workingStudentDifference)
  );
}

/**
 * 住民税の非課税判定に用いる世帯人数（本人 + 同一生計配偶者 + 扶養親族）。
 * 所得要件を超える扶養親族・同一生計配偶者は扶養親族に該当しないものとして数えない。
 * 老人扶養親族は所得入力が無いため所得要件を判定せず算入する（簡略化）。
 */
export function residentHousehold(
  familyData: FamilyData | null,
  rules: TaxRules,
): { count: number; hasDependents: boolean } {
  if (!familyData) {
    return { count: 1, hasDependents: false };
  }
  const threshold = rules.dependentIncomeThresholdResident();
  let householdCount = 1;
  if (familyData.spouse && familyData.spouseIncome * TEN_THOUSAND <= threshold) {
    householdCount += 1;
  }
  householdCount += qualifyingCount(familyData.dependents, familyData.dependentIncomes, threshold);
  householdCount += qualifyingCount(
    familyData.specificDependents,
    familyData.specificDependentIncomes,
    threshold,
  );
  householdCount += count(familyData.elderlyDependents);
  householdCount += count(familyData.dependentsUnder16);
  return { count: householdCount, hasDependents: householdCount > 1 };
}
